import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Types } from 'mongoose';
import { BehaviorSubject, Observable, take } from 'rxjs';

import { ENDPOINTS } from '@shared/constants';
import { SaaSTenantDataStoreResponseDTO } from '@shared/dtos';
import {
  CommonResponseDTO,
  ISaasContactVerification,
  ISaaSCreateTenantRequest,
  ISaaSTenantConfig,
  ISaaSTenantResponse,
  ISaaSUpdateTenantRequest,
  ITenantStorageQuota,
} from '@shared/interfaces';
import { generateURL } from '@shared/utils';

import { CurrenciesService } from '../../setup/currencies/services/currencies.service';
import { ClientDataService } from '../../setup/general/services/client-data.service';
import { INTERCEPTOR_SKIPPERS } from '../../../constants';

@Injectable({
  providedIn: 'root',
})
export class SaaSTenantService {
  constructor(
    private http: HttpClient,
    private currenciesService: CurrenciesService,
    private clientDataService: ClientDataService
  ) {}

  public readonly dataStore = new BehaviorSubject<ISaaSTenantResponse>(null);

  setupTenant(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.getOwnTenantDetails().subscribe({
        next: (res) => {
          const config = res.data.config;
          this.dataStore.next(res.data);
          if (config) {
            if (config.currency) {
              this.currenciesService.setBaseCurrency(
                config?.currency?.toString()
              );
            }
            this.clientDataService.dataStore.pipe(take(1)).subscribe((data) => {
              const clientInformation = data;

              this.clientDataService.updateData({
                ...clientInformation,
                ...config,
              });
              resolve();
            });
          } else {
            resolve();
          }
        },
        error: (err) => reject(new Error(err.message)),
      });
    });
  }

  getAllTenants(
    params: HttpParams
  ): Observable<CommonResponseDTO<ISaaSTenantResponse[]>> {
    const config = { params };
    const url = generateURL({ endpoint: ENDPOINTS.SAAS_TENANT });
    return this.http.get<CommonResponseDTO<ISaaSTenantResponse[]>>(url, config);
  }

  getSingleTenant(
    id: string,
    skipErrorInterceptor = false
  ): Observable<CommonResponseDTO<ISaaSTenantResponse>> {
    const url = generateURL({
      endpoint: ENDPOINTS.SAAS_TENANT_FETCH_ONE,
      params: { id },
    });
    const headers = skipErrorInterceptor
      ? new HttpHeaders().set(INTERCEPTOR_SKIPPERS.ERROR_INTERCEPTOR, 'valid')
      : undefined;
    return this.http.get<CommonResponseDTO<ISaaSTenantResponse>>(url, {
      headers,
    });
  }

  getTenantStorageQuota(): Observable<CommonResponseDTO<ITenantStorageQuota>> {
    const url = generateURL({
      endpoint: ENDPOINTS.SAAS_TENANT_STORAGE_QUOTA,
    });
    return this.http.get<CommonResponseDTO<ITenantStorageQuota>>(url);
  }

  checkIsContactUnique(params: HttpParams) {
    const url = generateURL({
      endpoint: ENDPOINTS.CHECK_SAAS_TENANT_EMAIL_UNIQUENESS,
    });
    return this.http.get<CommonResponseDTO<ISaasContactVerification>>(url, {
      params,
    });
  }

  createNewTenant(
    data: ISaaSCreateTenantRequest
  ): Observable<CommonResponseDTO<ISaaSTenantResponse>> {
    const url = generateURL({ endpoint: ENDPOINTS.SAAS_TENANT });
    return this.http.post<CommonResponseDTO<ISaaSTenantResponse>>(url, data);
  }

  updateTenant(
    id: string,
    data: ISaaSUpdateTenantRequest
  ): Observable<CommonResponseDTO<ISaaSTenantResponse>> {
    const url = generateURL({
      endpoint: ENDPOINTS.SAAS_TENANT_UPDATE_ONE,
      params: { id },
    });
    return this.http.put<CommonResponseDTO<ISaaSTenantResponse>>(url, data);
  }

  deleteTenant(id: string) {
    const url = generateURL({
      endpoint: ENDPOINTS.SAAS_TENANT_DELETE_ONE,
      params: { id },
    });
    return this.http.delete<CommonResponseDTO<ISaaSTenantResponse>>(url);
  }

  restoreTenant(id: string) {
    const url = generateURL({
      endpoint: ENDPOINTS.SAAS_TENANT_RESTORE_ONE,
      params: { id },
    });
    return this.http.patch<CommonResponseDTO<ISaaSTenantResponse>>(url, {});
  }

  blockTenant(id: string) {
    const url = generateURL({
      endpoint: ENDPOINTS.SAAS_TENANT_BLOCK_ONE,
      params: { id },
    });
    return this.http.patch<CommonResponseDTO<ISaaSTenantResponse>>(url, {});
  }

  unBlockTenant(id: string) {
    const url = generateURL({
      endpoint: ENDPOINTS.SAAS_TENANT_UNBLOCK_ONE,
      params: { id },
    });
    return this.http.patch<CommonResponseDTO<ISaaSTenantResponse>>(url, {});
  }

  updateTenantSubscription(plan: string, tenant: string) {
    const url = generateURL({
      endpoint: ENDPOINTS.SAAS_TENANT_CHANGE_SUBSCRIPTIONS,
    });
    return this.http.patch<CommonResponseDTO<ISaaSTenantResponse>>(url, {
      plan,
      tenant,
    });
  }

  getOwnTenantDetails() {
    const url = generateURL({
      endpoint: ENDPOINTS.SAAS_TENANT_CONFIG,
    });
    return this.http.get<CommonResponseDTO<ISaaSTenantResponse>>(url, {});
  }

  updateTenantConfig(config: ISaaSTenantConfig) {
    const url = generateURL({
      endpoint: ENDPOINTS.SAAS_TENANT_CONFIG,
    });

    return this.http.patch<CommonResponseDTO<ISaaSTenantResponse>>(url, config);
  }

  public getTenantDataConfig(
    _id?: Types.ObjectId
  ): Observable<CommonResponseDTO<SaaSTenantDataStoreResponseDTO>> {
    const url = generateURL({
      endpoint: ENDPOINTS.SAAS_TENANT_SINGLE_CONFIGURATION,
      params: { id: _id?.toString() },
    });

    return this.http.get<CommonResponseDTO<SaaSTenantDataStoreResponseDTO>>(
      url
    );
  }
}
